import { useState, useRef } from "react";
import {
    Wand2, Upload, Loader2, FileDown, X, Sparkles, Image as ImageIcon,
    Plus, Trash2, Sun, Car, Zap,
} from "lucide-react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import NltOfferGenerator from "./NltOfferGenerator";

const SECTORS = [
    { key: "noleggio_lungo_termine", label: "Noleggio Lungo Termine", icon: Car, disabled: false },
    { key: "energy_quoter", label: "Energy Quoter", icon: Zap, disabled: true },
];

export default function BrandedOfferGenerator() {
    const [sector, setSector] = useState("noleggio_lungo_termine");

    return (
        <div className="max-w-5xl mx-auto" data-testid="branded-offer-page">
            <div className="mb-6">
                <div className="inline-flex items-center gap-2 bg-[#C7FF2C]/30 text-[#0C4650] px-3 py-1 rounded-full text-xs font-bold">
                    <Wand2 className="w-3 h-3" /> BO STUDIO · GENERATORE PDF
                </div>
                <h1 className="mt-3 text-3xl sm:text-4xl font-black text-[#0C4650]">
                    Genera un'offerta accattivante
                </h1>
                <p className="text-gray-600 mt-1 max-w-2xl">
                    {sector === "noleggio_lungo_termine"
                        ? "Per il Noleggio LT puoi configurare condizioni contrattuali, franchigie, servizi inclusi e generare il PDF dedicato."
                        : "Crea al volo un PDF brandizzato 1 pagina con 2 immagini (esterno + interno), benefici fiscali per settore e contatti."}
                </p>
            </div>

            {/* Sector tabs */}
            <div className="card p-4 mb-5">
                <div className="text-[10px] tracking-widest font-bold text-[#898A8D] mb-3">
                    SETTORE
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                    {SECTORS.map((s) => {
                        const Icon = s.icon;
                        const active = sector === s.key;
                        const disabled = s.disabled;
                        return (
                            <button key={s.key}
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => !disabled && setSector(s.key)}
                                    data-testid={`bo-sector-${s.key}`}
                                    className={`relative text-left rounded-xl p-3 transition-all border-2 ${
                                        disabled
                                            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-60"
                                            : active
                                                ? "bg-[#0C4650] text-white border-[#0C4650]"
                                                : "bg-white text-[#0C4650] border-gray-200 hover:border-[#0C4650]"
                                    }`}>
                                <Icon className={`w-4 h-4 ${
                                    disabled ? "text-gray-400"
                                        : active ? "text-[#C7FF2C]" : "text-[#0C4650]"
                                }`} />
                                <div className="font-bold text-sm mt-1">{s.label}</div>
                                {disabled && (
                                    <span className="absolute top-2 right-2 bg-[#C7FF2C] text-[#0C4650] text-[9px] font-black px-1.5 py-0.5 rounded">
                                        IN ARRIVO
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {sector === "noleggio_lungo_termine" ? (
                <NltOfferGenerator />
            ) : (
                <ComingSoonPlaceholder sectorLabel={
                    SECTORS.find(s => s.key === sector)?.label || sector
                } />
            )}
        </div>
    );
}

function ComingSoonPlaceholder({ sectorLabel }) {
    return (
        <div className="card p-12 text-center" data-testid="bo-coming-soon">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#C7FF2C]/40 mb-4">
                <Sparkles className="w-8 h-8 text-[#0C4650]" />
            </div>
            <h2 className="text-2xl font-black text-[#0C4650] mb-2">
                {sectorLabel} — In arrivo
            </h2>
            <p className="text-gray-600 max-w-md mx-auto">
                Il generatore PDF dedicato per <b>{sectorLabel}</b> è in fase di sviluppo.
                Per ora è disponibile solo il flusso <b>Noleggio Lungo Termine</b>.
            </p>
        </div>
    );
}

function GenericOfferForm({ sector }) {
    const [form, setForm] = useState({
        title: "",
        subtitle: "",
        description: "",
        price: "",
        monthly_price: "",
        highlights: [],
        client_name: "",
        client_email: "",
        client_phone: "",
        fiscal_benefits_override: [],
    });
    const [hlInput, setHlInput] = useState("");
    const [exteriorImg, setExteriorImg] = useState(null);
    const [interiorImg, setInteriorImg] = useState(null);
    const [generating, setGenerating] = useState(false);
    const extInput = useRef(null);
    const intInput = useRef(null);

    const upload = async (file, which) => {
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data: res } = await api.post("/admin/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            });
            const ref = { id: res.id, filename: res.filename, url: res.url };
            if (which === "exterior") setExteriorImg(ref);
            else setInteriorImg(ref);
            toast.success(`${which === "exterior" ? "Foto esterno" : "Foto interno"} caricata`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload fallito");
        }
    };

    const addHighlight = () => {
        const v = hlInput.trim();
        if (!v) return;
        setForm({ ...form, highlights: [...form.highlights, v] });
        setHlInput("");
    };
    const removeHighlight = (i) =>
        setForm({ ...form, highlights: form.highlights.filter((_, idx) => idx !== i) });

    const generate = async () => {
        if (!form.title.trim() || !form.description.trim()) {
            toast.error("Titolo e descrizione sono obbligatori");
            return;
        }
        setGenerating(true);
        try {
            const payload = {
                sector: sector,
                title: form.title.trim(),
                subtitle: form.subtitle.trim() || null,
                description: form.description.trim(),
                price: form.price.trim() || null,
                monthly_price: form.monthly_price.trim() || null,
                highlights: form.highlights,
                exterior_image_file_id: exteriorImg?.id || null,
                interior_image_file_id: interiorImg?.id || null,
                client_name: form.client_name.trim() || null,
                client_email: form.client_email.trim() || null,
                client_phone: form.client_phone.trim() || null,
                fiscal_benefits_override:
                    form.fiscal_benefits_override.filter((s) => s.trim().length > 0)
                        .length > 0
                        ? form.fiscal_benefits_override.filter((s) => s.trim().length > 0)
                        : null,
            };
            const resp = await api.post("/admin/branded-offer-pdf", payload, {
                responseType: "blob", timeout: 45000,
            });
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Offerta-${form.title.slice(0, 40)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 800);
            toast.success("PDF generato e scaricato");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore generazione PDF");
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-5">
                    {/* Contenuti */}
                    <div className="card p-5 sm:p-6 space-y-4">
                        <h2 className="text-lg font-black text-[#0C4650]">Contenuti dell'offerta</h2>
                        <Field label="Titolo *" testid="bo-title">
                            <input className="field"
                                   value={form.title}
                                   onChange={(e) => setForm({ ...form, title: e.target.value })}
                                   placeholder="Es: Jeep Avenger Hybrid - 48 mesi" />
                        </Field>
                        <Field label="Sottotitolo" testid="bo-subtitle">
                            <input className="field"
                                   value={form.subtitle}
                                   onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                                   placeholder="Una proposta esclusiva, tutto incluso" />
                        </Field>
                        <Field label="Descrizione *" testid="bo-description">
                            <textarea rows={4} className="field"
                                      value={form.description}
                                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                                      placeholder="Descrivi l'offerta in modo chiaro e coinvolgente…" />
                        </Field>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <Field label="Prezzo (totale o IVA)" testid="bo-price">
                                <input className="field"
                                       value={form.price}
                                       onChange={(e) => setForm({ ...form, price: e.target.value })}
                                       placeholder="€ 12.500" />
                            </Field>
                            <Field label="Rata mensile" testid="bo-monthly">
                                <input className="field"
                                       value={form.monthly_price}
                                       onChange={(e) => setForm({ ...form, monthly_price: e.target.value })}
                                       placeholder="€ 395 / mese" />
                            </Field>
                        </div>
                    </div>

                    {/* Highlights */}
                    <div className="card p-5 sm:p-6">
                        <h2 className="text-lg font-black text-[#0C4650]">Punti chiave</h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Massimo 6 punti chiave che caratterizzano l'offerta.
                        </p>
                        <div className="mt-3 flex gap-2">
                            <input className="field flex-1"
                                   value={hlInput}
                                   onChange={(e) => setHlInput(e.target.value)}
                                   onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addHighlight())}
                                   placeholder="Es: Anticipo ZERO · Bollo e assicurazione inclusi"
                                   data-testid="bo-hl-input" />
                            <button onClick={addHighlight} className="btn-outline" data-testid="bo-hl-add">
                                <Plus className="w-4 h-4" /> Aggiungi
                            </button>
                        </div>
                        {form.highlights.length > 0 && (
                            <ul className="mt-3 space-y-1">
                                {form.highlights.map((h, i) => (
                                    // key={i} è intenzionale: removeHighlight usa l'indice come riferimento di posizione
                                    <li key={i} data-testid={`bo-hl-${i}`}
                                        className="flex items-center justify-between gap-2 p-2 rounded-lg bg-[#f7f7f5]">
                                        <span className="text-sm text-[#0C4650]">{h}</span>
                                        <button onClick={() => removeHighlight(i)}
                                                className="text-rose-600 hover:text-rose-700"
                                                aria-label="Rimuovi">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Immagini */}
                    <div className="card p-5 sm:p-6">
                        <h2 className="text-lg font-black text-[#0C4650]">Immagini</h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Carica due foto: l'esterno (banda hero) e l'interno (dettaglio).
                        </p>
                        <div className="grid sm:grid-cols-2 gap-3 mt-3">
                            <ImageSlot
                                label="Foto esterno (hero)"
                                image={exteriorImg}
                                onUpload={(f) => upload(f, "exterior")}
                                onRemove={() => setExteriorImg(null)}
                                inputRef={extInput}
                                testid="bo-img-ext"
                            />
                            <ImageSlot
                                label="Foto interno (dettaglio)"
                                image={interiorImg}
                                onUpload={(f) => upload(f, "interior")}
                                onRemove={() => setInteriorImg(null)}
                                inputRef={intInput}
                                testid="bo-img-int"
                            />
                        </div>
                    </div>

                    {/* Contatto sul PDF */}
                    <div className="card p-5 sm:p-6">
                        <h2 className="text-lg font-black text-[#0C4650]">Referente sul PDF</h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Apparirà nel footer come contatto commerciale. Se vuoto, usa i tuoi dati admin.
                        </p>
                        <div className="grid sm:grid-cols-3 gap-3 mt-3">
                            <Field label="Nome" testid="bo-ref-name">
                                <input className="field"
                                       value={form.client_name}
                                       onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                                       placeholder="Es: Sara Bianchi" />
                            </Field>
                            <Field label="Email" testid="bo-ref-email">
                                <input className="field"
                                       value={form.client_email}
                                       onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                                       placeholder="sara@scintilla.it" />
                            </Field>
                            <Field label="Telefono" testid="bo-ref-phone">
                                <input className="field"
                                       value={form.client_phone}
                                       onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                                       placeholder="+39 333 ..." />
                            </Field>
                        </div>
                    </div>
                </div>

                {/* Sidebar CTA */}
                <div>
                    <div className="card p-5 sticky top-4 border-2 border-[#C7FF2C]">
                        <div className="text-[10px] tracking-widest font-bold text-[#898A8D]">
                            ANTEPRIMA RIASSUNTIVA
                        </div>
                        <div className="mt-2 text-lg font-black text-[#0C4650] leading-tight">
                            {form.title || "Titolo offerta…"}
                        </div>
                        {form.subtitle && (
                            <div className="text-sm text-gray-500 mt-0.5">{form.subtitle}</div>
                        )}
                        {(form.monthly_price || form.price) && (
                            <div className="mt-3 text-2xl font-black text-[#0C4650]">
                                {form.monthly_price || form.price}
                            </div>
                        )}
                        <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-gray-600">
                            <div className="flex items-center gap-1">
                                <ImageIcon className="w-3 h-3" /> Ext: {exteriorImg ? "✓" : "—"}
                            </div>
                            <div className="flex items-center gap-1">
                                <ImageIcon className="w-3 h-3" /> Int: {interiorImg ? "✓" : "—"}
                            </div>
                            <div>HL: {form.highlights.length}</div>
                            <div>Sett: {sector.split("_")[0]}</div>
                        </div>
                        <button onClick={generate} disabled={generating}
                                className="btn-primary w-full mt-5 disabled:opacity-50"
                                data-testid="bo-generate">
                            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                            Genera e scarica
                        </button>
                        <p className="text-[10px] text-gray-400 mt-2 text-center">
                            <Sparkles className="w-3 h-3 inline-block" /> Non viene salvato a catalogo
                        </p>
                    </div>
                </div>
            </div>
    );
}

const Field = ({ label, children, testid }) => (
    <div data-testid={testid}>
        <label className="label">{label}</label>
        {children}
    </div>
);

const ImageSlot = ({ label, image, onUpload, onRemove, inputRef, testid }) => {
    const token = localStorage.getItem("scintilla_token") || "";
    const imgUrl = image?.url
        ? `${API}${image.url}${image.url.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`
        : null;
    return (
        <div data-testid={testid}>
            <div className="text-xs font-bold text-[#0C4650] mb-1">{label}</div>
            {image ? (
                <div className="relative rounded-xl overflow-hidden h-36 bg-[#f7f7f5]">
                    {imgUrl && (
                        <div className="absolute inset-0 bg-cover bg-center"
                             style={{ backgroundImage: `url(${imgUrl})` }} />
                    )}
                    <button onClick={onRemove}
                            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white text-red-500 flex items-center justify-center shadow-md"
                            data-testid={`${testid}-remove`}>
                        <X className="w-3.5 h-3.5" />
                    </button>
                    <div className="absolute bottom-2 left-2 text-[10px] bg-black/60 text-white px-2 py-0.5 rounded">
                        {image.filename}
                    </div>
                </div>
            ) : (
                <label className="flex flex-col items-center justify-center gap-1 border-2 border-dashed border-[#0C4650]/30 hover:border-[#0C4650] rounded-xl py-9 cursor-pointer bg-[#f7f7f5]">
                    <Upload className="w-5 h-5 text-[#0C4650]" />
                    <span className="text-xs font-bold text-[#0C4650]">Carica</span>
                    <input ref={inputRef} type="file" accept="image/*" className="hidden"
                           onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
                           data-testid={`${testid}-input`} />
                </label>
            )}
        </div>
    );
};
